// The tree of the Scripts IDE (spec/dashboard.md section 10.8): the branch's extensions by group (collapsible), each with its
// enabled switch, its log-count badge and an error badge when the host failed to load it.
import { useState, type ReactNode } from 'react';
import type { components } from '../../api/schema';
import type { LoadError } from '../../extensions/protocol';
import { Badge } from '../../ui/Chip';
import { ChevronDown, ChevronRight } from '../../ui/icons';
import { Switch } from '../../ui/inputs';

export type ExtensionSummary = components['schemas']['ExtensionSummaryDto'];

export interface ScriptsTreeProps {
  items: ExtensionSummary[];
  active: string | null;
  logCounts: Map<string, number>;
  errors: LoadError[];
  dirty: Set<string>;
  canWrite: boolean;
  toggling: Set<string>;
  onOpen: (name: string) => void;
  onToggle: (name: string, enabled: boolean) => void;
  header: ReactNode;
  footer?: ReactNode;
}

export const DEFAULT_GROUP_LABEL = 'Ungrouped';

export function groupsOf(items: ExtensionSummary[]): { group: string; items: ExtensionSummary[] }[] {
  const by = new Map<string, ExtensionSummary[]>();
  for (const e of items) {
    const g = e.group ?? '';
    by.set(g, [...(by.get(g) ?? []), e]);
  }
  return Array.from(by.entries())
    .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
    .map(([group, list]) => ({ group, items: list.sort((x, y) => x.name.localeCompare(y.name)) }));
}

export function ScriptsTree({ items, active, logCounts, errors, dirty, canWrite, toggling, onOpen, onToggle, header, footer }: ScriptsTreeProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleGroup = (g: string) =>
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  return (
    <nav className="pane scripts-tree" data-testid="scripts-tree" aria-label="Scripts">
      <div className="pane-head scripts-tree-head">
        <h2>All scripts</h2>
        <span className="chip">{items.length}</span>
      </div>
      <div className="scripts-tree-body">
        {header}
        <ul className="scripts-groups">
          {groupsOf(items).map(({ group, items: list }) => {
            const isCollapsed = collapsed.has(group);
            const label = group || DEFAULT_GROUP_LABEL;
            return (
              <li key={group} className="scripts-group" data-group={group}>
                <button type="button" className="scripts-group-toggle" aria-expanded={!isCollapsed} onClick={() => toggleGroup(group)}>
                  {isCollapsed ? <ChevronRight aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
                  <span className="scripts-group-name">{label}</span>
                  <span className="scripts-group-count">{list.length}</span>
                </button>
                {!isCollapsed && (
                  <ul className="scripts-rows" aria-label={label}>
                    {list.map((e) => {
                      const count = logCounts.get(e.name) ?? 0;
                      const failed = errors.filter((x) => x.name === e.name);
                      return (
                        <li key={e.name} className={`scripts-row${active === e.name ? ' active' : ''}${e.enabled ? '' : ' disabled'}`} data-testid={`script-row-${e.name}`}>
                          <button type="button" className="scripts-row-name" aria-current={active === e.name ? 'page' : undefined} title={e.description ?? undefined} onClick={() => onOpen(e.name)}>
                            {e.name}
                            {dirty.has(e.name) && <span className="scripts-dirty" title="Unsaved changes" aria-label="unsaved" role="img" />}
                          </button>
                          {count > 0 && <Badge title={`${count} log ${count === 1 ? 'entry' : 'entries'}`}>{count}</Badge>}
                          {failed.length > 0 && (
                            <Badge tone="danger" title={failed.map((f) => (f.line ? `line ${f.line}: ${f.message}` : f.message)).join('\n')}>
                              error
                            </Badge>
                          )}
                          <Switch aria-label={`Enabled ${e.name}`} checked={e.enabled} disabled={!canWrite || toggling.has(e.name)} onChange={(v) => onToggle(e.name, v)} />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {footer}
    </nav>
  );
}
